import { useCallback, useEffect, useState } from 'react'

import { ProductReview, ProductReviewVote } from './types'

type PendingAuthAction = { type: 'review' } | { type: 'vote'; reviewId: string; vote: ProductReviewVote }

type UseProductReviewsModalsParams = {
	token?: string
	onVote: (reviewId: string, vote: ProductReviewVote) => Promise<ProductReview | undefined>
}

export const useProductReviewsModals = ({ token, onVote }: UseProductReviewsModalsParams) => {
	const [isReviewFormOpen, setIsReviewFormOpen] = useState(false)
	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
	const [pendingAuthAction, setPendingAuthAction] = useState<PendingAuthAction | null>(null)
	const [submittedRating, setSubmittedRating] = useState<number | null>(null)
	const [isModerationModalOpen, setIsModerationModalOpen] = useState(false)
	const [mediaModalIndex, setMediaModalIndex] = useState<number | null>(null)

	const requestReviewForm = useCallback(() => {
		if (token) setIsReviewFormOpen(true)
		else {
			setPendingAuthAction({ type: 'review' })
			setIsAuthModalOpen(true)
		}
	}, [token])

	const requestVote = useCallback(
		(reviewId: string, vote: ProductReviewVote) => {
			if (token) return onVote(reviewId, vote)
			else {
				setPendingAuthAction({ type: 'vote', reviewId, vote })
				setIsAuthModalOpen(true)
			}

			return undefined
		},
		[onVote, token]
	)

	useEffect(() => {
		if (!token || !pendingAuthAction) return

		setIsAuthModalOpen(false)
		setPendingAuthAction(null)
		if (pendingAuthAction.type === 'review') setIsReviewFormOpen(true)
		else onVote(pendingAuthAction.reviewId, pendingAuthAction.vote)
	}, [onVote, pendingAuthAction, token])

	const closeAuthModal = () => {
		setIsAuthModalOpen(false)
		setPendingAuthAction(null)
	}

	const openModerationModal = () => {
		setSubmittedRating(null)
		setIsModerationModalOpen(true)
	}

	return {
		reviewForm: {
			isOpen: isReviewFormOpen,
			request: requestReviewForm,
			close: () => setIsReviewFormOpen(false)
		},
		authorization: {
			isOpen: isAuthModalOpen,
			close: closeAuthModal
		},
		success: {
			rating: submittedRating,
			open: setSubmittedRating,
			close: () => setSubmittedRating(null)
		},
		moderation: {
			isOpen: isModerationModalOpen,
			open: () => setIsModerationModalOpen(true),
			openFromSuccess: openModerationModal,
			close: () => setIsModerationModalOpen(false)
		},
		media: {
			index: mediaModalIndex,
			open: setMediaModalIndex,
			close: () => setMediaModalIndex(null)
		},
		requestVote
	}
}
